import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, Spinner } from '@lensora/ui'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'


import { api, ApiError } from '@/lib/api'
import { authClient } from '@/lib/auth'

// Lands here after a Google OAuth signup: the Supabase session exists but no
// profile does yet. Collects the company name and creates the profile +
// end_client under the shared app tenant, which also grants the free signup
// trial credits immediately — unlike the password-signup path, which now
// gates that grant behind email confirmation, OAuth already has a
// provider-verified mailbox, so there is nothing left to confirm.
export function SignupCompletePage() {
  const navigate = useNavigate()
  const [checking, setChecking] = React.useState(true)
  const [companyName, setCompanyName] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    void (async () => {
      const session = await authClient.getSession()
      if (cancelled) return
      if (!session?.user) {
        navigate('/login', { replace: true })
        return
      }
      // Profile already exists (normal sign-in via Google) — go straight in
      try {
        await api.get('/api/auth/me')
        if (!cancelled) window.location.href = '/dashboard'
        return
      } catch {
        // No profile yet — show the completion form
      }
      if (!cancelled) setChecking(false)
    })()
    return () => {
      cancelled = true
    }
  }, [navigate])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      await api.post('/api/auth/signup/complete', { companyName })
      // No checkout here — the account is created with free trial credits and
      // goes straight into onboarding. Checkout happens later, from the
      // billing page or the paywall once the trial credits are spent.
      window.location.href = '/dashboard'
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ALREADY_COMPLETE') {
        window.location.href = '/dashboard'
        return
      }
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.')
      setIsLoading(false)
    }
  }

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-raised">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-raised p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 rounded-lg bg-brand-primary" />
          <h1 className="text-2xl font-bold text-text">Almost there</h1>
          <p className="mt-1 text-sm text-text-muted">One last detail to finish setting up.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Name your workspace</CardTitle>
            <CardDescription>Usually your company or brand name.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">Company name</Label>
                <Input
                  id="company-name"
                  placeholder="Acme Ltd"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>
              {error && <p className="text-sm text-feedback-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Setting up…' : 'Continue'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
